import { ArrowRight, BarChart3, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { useAuth } from "@/features/auth/useAuth";
import {
  canAccessKpiDepartment,
  canCollectKpis,
  visibleKpiDefs,
} from "@/features/kpi/access";
import {
  KpiAutomationSummary,
  KpiBackendNotice,
  KpiPeriodToolbar,
  KpiTestGuide,
} from "@/features/kpi/components";
import {
  DEPARTMENTS,
  KPI_DEFS,
  kpiOnTarget,
} from "@/features/kpi/definitions";
import { buildKpiCsv, downloadKpiCsv } from "@/features/kpi/export";
import { useKpiWorkspace } from "@/features/kpi/useKpiWorkspace";

export default function InternalKpis() {
  const { user } = useAuth();
  const workspace = useKpiWorkspace();
  const visible = visibleKpiDefs(user);
  const departments = DEPARTMENTS.filter((department) =>
    canAccessKpiDepartment(user, department.id),
  );
  const canCollect = canCollectKpis(user);

  function statsFor(ids: string[]) {
    const entered = ids.filter((id) => workspace.current.has(id)).length;
    const red = ids.filter((id) => {
      const def = KPI_DEFS.find((item) => item.id === id);
      const entry = workspace.current.get(id);
      return def && entry ? kpiOnTarget(def, entry.value) === false : false;
    }).length;
    const automatic = ids.filter(
      (id) => KPI_DEFS.find((item) => item.id === id)?.automation === "auto",
    ).length;
    return { entered, red, automatic };
  }

  function exportCsv() {
    downloadKpiCsv(
      `kpi-${workspace.periodType}-${workspace.periodStart}.csv`,
      buildKpiCsv(
        visible,
        workspace.current,
        workspace.previous,
        workspace.periodType,
        workspace.periodStart,
      ),
    );
  }

  return (
    <>
      <Seo title="KPIs" noindex />
      <DemoBanner />
      <div>
        <h1 className="text-2xl font-bold">KPI management</h1>
        <p className="mt-1 max-w-3xl text-sm text-brand-500">
          Open a Framework department to review its metrics and data sources, or open
          the Scorecard for the executive division view. Automatic KPIs pull from
          Fleetio, Samsara, and QuickManage. Load automatic values reuses the last
          good pull for 6 hours; Refresh from systems asks the live APIs again.
        </p>
        <div className="mt-3"><KpiAutomationSummary /></div>
      </div>

      <KpiBackendNotice backend={workspace.backend} />
      <KpiPeriodToolbar
        periodType={workspace.periodType}
        periodStart={workspace.periodStart}
        onPeriodType={workspace.switchPeriodType}
        onPeriodStart={workspace.setPeriodStart}
        entered={visible.filter((def) => workspace.current.has(def.id)).length}
        total={visible.length}
        dirtyCount={0}
        saving={false}
        saved={false}
        onSave={() => undefined}
        showSave={false}
        collecting={workspace.collecting}
        onCollect={canCollect ? (force) => void workspace.collectAuto(force) : undefined}
        lastCollectAt={workspace.lastCollectAt}
        collectFresh={workspace.collectFresh}
        onExport={exportCsv}
      />
      {workspace.collectNote && (
        <FeedbackMessage tone="info" className="mt-4">{workspace.collectNote}</FeedbackMessage>
      )}
      {workspace.error && (
        <FeedbackMessage tone="error" className="mt-4">{workspace.error}</FeedbackMessage>
      )}
      {canCollect && <KpiTestGuide />}

      {workspace.loading ? (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <CardSkeleton rows={3} />
          <CardSkeleton rows={3} />
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {departments.map((department) => {
              const defs = KPI_DEFS.filter((def) => def.department === department.id);
              const stats = statsFor(defs.map((def) => def.id));
              return (
                <Link
                  key={department.id}
                  to={`/app/internal/kpis/section/${department.id}`}
                  className="group"
                >
                  <Card className="h-full transition-colors group-hover:border-accent-400">
                    <CardHeader className="flex flex-row items-start justify-between gap-3">
                      <div>
                        <div className="mb-3 inline-flex rounded-lg bg-brand-100 p-2 text-brand-700">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <CardTitle>{department.title}</CardTitle>
                      </div>
                      <ArrowRight className="mt-2 h-5 w-5 text-brand-300 transition-transform group-hover:translate-x-1 group-hover:text-accent-600" />
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-brand-500">{department.blurb}</p>
                      <div className="mt-5 grid grid-cols-3 gap-3 border-t border-brand-100 pt-4 text-center">
                        <CardStat value={`${stats.entered}/${defs.length}`} label="Entered" />
                        <CardStat value={String(stats.red)} label="Red" danger={stats.red > 0} />
                        <CardStat value={String(stats.automatic)} label="Auto" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <Link to="/app/internal/kpis/scorecard" className="group mt-4 block">
            <Card className="border-brand-300 bg-brand-900 text-white transition-colors group-hover:border-accent-400">
              <CardContent className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-white/10 p-3"><BarChart3 className="h-6 w-6" /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">Executive Scorecard</h2>
                      <Badge variant="accent">Filtered view</Badge>
                    </div>
                    <p className="mt-1 max-w-2xl text-sm text-white/65">
                      The same Framework values, regrouped by company, tanker, dry van,
                      sales, and accounting—with division, weekly ops, and red-only filters.
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-6 w-6 shrink-0 transition-transform group-hover:translate-x-1" />
              </CardContent>
            </Card>
          </Link>
        </>
      )}
    </>
  );
}

function CardStat({
  value,
  label,
  danger = false,
}: {
  value: string;
  label: string;
  danger?: boolean;
}) {
  return (
    <div>
      <div className={danger ? "text-lg font-bold text-red-600" : "text-lg font-bold text-brand-900"}>
        {value}
      </div>
      <div className="text-xs text-brand-400">{label}</div>
    </div>
  );
}
